import * as amplitude from "@amplitude/analytics-browser";
import { useApolloClient } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Code,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import type { FormikErrors } from "formik";
import { Form, FormikProvider, useFormik } from "formik";
import { LexicalEditor } from "lexical";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import {
  RiDeleteBin6Line,
  RiFlaskLine,
  RiLightbulbLine,
  RiMagicLine,
  RiTranslate2,
} from "react-icons/ri";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { PaginatedTabList } from "src/components/Layout/PaginatedTabList";
import { MaybeLink } from "src/components/Links/MaybeLink";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import {
  RecipientsTable,
  RecipientsTableDataType,
} from "src/components/Table/RecipientsTable";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import { convertPlainTextToHtml } from "src/components/WysiwygEditor/lexical/utils/converters";
import { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { getBestAvailableLanguage } from "src/hooks/useGetEnglishLanguageName";
import { useGetMessageRecipients } from "src/hooks/useGetMessageRecipients";
import { useMessageRichContent } from "src/hooks/useMessageRichContent";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { NewMessageState } from "src/hooks/useSendMessage";
import { useTranslationLanguageSelector } from "src/hooks/useTranslationLanguageSelector";
import useUser from "src/hooks/useUser";
import * as breadcrumb from "src/services/breadcrumb";
import * as Person from "src/services/person";
import { isNotNull } from "src/services/predicates";
import * as Url from "src/services/url";
import { Status } from "src/types/authData";
import * as GQL from "src/types/graphql";
import { RichContentDocument } from "src/types/messages/content";
import { GET_FORM_MESSAGES } from "../forms/graphql/queries";
import { MessageForm } from "./components/MessageForm_v2";
import { SEND_MESSAGE } from "./graphql/mutations";
import { GET_SENDER_ID } from "./graphql/queries";
import { useMessagePolish } from "./hooks/useMessagePolish";
import { useMessageTranslation } from "./hooks/useMessageTranslation";
import { MessageContent, MessageFormType } from "./schemas";
import { validateMessages } from "./validators";

export const NewMessage = () => {
  const featureFlag = useFlags(["segmented_message_warning"]);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const toast = useAvelaToast();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const flags = useFlags(["ai-messaging-translation", "ai-messaging-polish"]);

  const [smsEditor, setSmsEditor] = useState<LexicalEditor>();
  const [emailEditor, setEmailEditor] = useState<LexicalEditor>();
  const emailEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) {
      setEmailEditor(editor);
    }
  };
  const smsEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) setSmsEditor(editor);
  };
  const state = location.state as NewMessageState | undefined;
  const organizationId = useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );
  const apolloClient = useApolloClient();
  const { selectedNavFormTemplate } = useFormTemplates();

  const [currentTranslationIndex, setCurrentTranslationIndex] =
    useState<number>(0);

  const {
    confirm: confirmTranslationLanguages,
    translationLanguageSelectorDialog,
  } = useTranslationLanguageSelector();

  const [formRecipients, isUnverifiedCommsEnabledForOrg] =
    useGetMessageRecipients(state?.formsIdList || []);

  const { remoteData: senderIdRemoteData } = useRemoteDataQuery<
    GQL.GetSenderId,
    GQL.GetSenderIdVariables
  >(GET_SENDER_ID, {
    variables: {
      userId: user.status === Status.OK ? user.data.id : "",
      organizationId: organizationId,
    },
    skip: user.status !== Status.OK || !organization.hasData(),
  });

  const [SendMessage] = useRemoteDataMutation<
    GQL.SendMessage,
    GQL.SendMessageVariables
  >(SEND_MESSAGE);

  const sendMessageInfo = useMemo(() => {
    let emailCount = 0;
    let smsCount = 0;
    const receiversInfosByLanguage: Record<
      string,
      GQL.message_adhoc_form_receiver_insert_input[]
    > = {};

    formRecipients.forEach(({ formId, applicant, recipients }) => {
      recipients.forEach((guardian) => {
        const contactInformation = Person.getContactInformation(
          guardian,
          isUnverifiedCommsEnabledForOrg
        );
        const type = contactInformation.type;
        const language = getBestAvailableLanguage(
          guardian.preferred_language || "en"
        );

        if (!receiversInfosByLanguage[language]) {
          receiversInfosByLanguage[language] = [];
        }

        if (
          type === Person.ContactType.Email ||
          type === Person.ContactType.All
        ) {
          emailCount++;
          receiversInfosByLanguage[language].push({
            form_id: formId,
            applicant_id: applicant.id,
            receiver_person_id: guardian.id,
            receiver_contact_information: contactInformation.email_address,
            message_type: GQL.message_type_enum.email,
          });
        }

        if (
          type === Person.ContactType.Phone ||
          type === Person.ContactType.All
        ) {
          smsCount++;
          receiversInfosByLanguage[language].push({
            form_id: formId,
            applicant_id: applicant.id,
            receiver_person_id: guardian.id,
            receiver_contact_information: contactInformation.phone_number,
            message_type: GQL.message_type_enum.SMS,
          });
        }
      });
    });

    const translatableLanguages = _.uniq(
      Object.keys(receiversInfosByLanguage)
    ).filter((language) => language !== "en" && isNotNull(language));

    // Flatten for backward compatibility with mutation
    const receiversInfos = Object.values(receiversInfosByLanguage).flat();

    return {
      receiversInfosByLanguage,
      receiversInfos,
      translatableLanguages,
      emailCount,
      smsCount,
      hasRecipientsWithEmail: emailCount > 0,
      hasRecipientsWithPhone: smsCount > 0,
    };
  }, [formRecipients, isUnverifiedCommsEnabledForOrg]);

  const [translatedLanguages, setTranslatedLanguages] = useState<string[]>([]);

  // Group the receivers infos by the translated languages
  const receiversInfosByTranslatedLanguage = useMemo(() => {
    const source = sendMessageInfo.receiversInfosByLanguage;
    const translatedSet = new Set(translatedLanguages);
    const result: Record<
      string,
      GQL.message_adhoc_form_receiver_insert_input[]
    > = {};

    for (const [lang, infos] of Object.entries(source)) {
      const targetLang = translatedSet.has(lang) ? lang : "en";
      if (!result[targetLang]) result[targetLang] = [];
      result[targetLang].push(...infos);
    }
    return result;
  }, [sendMessageInfo.receiversInfosByLanguage, translatedLanguages]);

  const initialValues = useMemo<MessageFormType>(
    () =>
      ({
        messages: [
          {
            language: "en",
            language_name: "English",
            subject: "Message about your school form",
            email_lexical: undefined,
            sms_lexical: undefined,
          },
        ],
        email_attachments: [],
      } satisfies MessageFormType),
    []
  );

  const recipients: RecipientsTableDataType[] = useMemo(
    () =>
      formRecipients.flatMap(({ applicant, recipients }) =>
        recipients.flatMap((recipient) => {
          const contact = Person.getContactInformation(
            recipient,
            isUnverifiedCommsEnabledForOrg
          );
          const base = {
            id: recipient.id,
            full_name: `${recipient.first_name ?? ""} ${
              recipient.last_name ?? ""
            }`.trim(),
            preferred_language: recipient.preferred_language,
            applicant_name: applicant.first_name ?? "",
          };

          switch (contact.type) {
            case Person.ContactType.Email:
              return [{ ...base, contact_info: contact.email_address }];
            case Person.ContactType.Phone:
              return [{ ...base, contact_info: contact.phone_number }];
            case Person.ContactType.All:
              return [
                { ...base, contact_info: contact.email_address },
                { ...base, contact_info: contact.phone_number },
              ];
            default:
              return [];
          }
        })
      ),
    [formRecipients, isUnverifiedCommsEnabledForOrg]
  );

  const { uploadMessageDocument, getMessageDocument } = useMessageRichContent(
    emailEditor,
    smsEditor
  );

  const converter = useContentConversion({ variableMode: "expanded" });

  const messageContext = useMemo(() => {
    if (!selectedNavFormTemplate) return undefined;
    const template = selectedNavFormTemplate as {
      id: string;
      name: string;
      enrollment_period?: { id: string; name: string } | null;
    };
    const org = organization.hasData() ? organization.data : null;
    return {
      trigger_event_name: "Ad hoc",
      form_template: { id: template.id, name: template.name },
      enrollment_period: template.enrollment_period
        ? {
            id: template.enrollment_period.id,
            name: template.enrollment_period.name,
          }
        : null,
      organization: org ? { id: org.id, name: org.name } : null,
    } satisfies MessageContextSchema;
  }, [selectedNavFormTemplate, organization]);

  const discardChangesConfirmation = useConfirmationDialog({
    header: "Discard message?",
    body: "Are you sure you want to discard the message you've created?",
    confirmButton: { label: "Yes, discard message" },
    cancelButton: { label: "Cancel" },
  });

  const deleteTranslationConfirmation = useConfirmationDialog({
    header: "Remove translation?",
    body: "Are you sure you want to remove this translation?",
    confirmButton: { label: "Yes, remove" },
    cancelButton: { label: "Cancel" },
  });

  const handleSendMessageDocument = useCallback(
    async (
      subject: string,
      senderId: string,
      messageDocument: RichContentDocument,
      receiversInfos: GQL.message_adhoc_form_receiver_insert_input[]
    ) => {
      const { document_id } = await uploadMessageDocument(messageDocument);

      if (!document_id) {
        toast({
          title: "Error sending message",
          description: "There are no content to send",
        });
        return;
      }

      const messageObject: GQL.SendMessageVariables["message"] = {
        sender_person_id: senderId,
        organization_id: organizationId,
        message_adhoc_form_receivers: {
          data: receiversInfos,
        },
        message_adhoc_payload_sms: {
          data: {
            message_body: messageDocument.smsText || "",
          },
        },
        message_adhoc_payload_email: {
          data: {
            message_body: messageDocument.text || "",
            message_subject: subject,
          },
        },
        message_adhoc_payload_rich_contents: {
          data: [
            {
              content_id: document_id,
            },
          ],
        },
      };

      await SendMessage({
        variables: {
          message: messageObject,
        },
      });
    },
    [SendMessage, organizationId, toast, uploadMessageDocument]
  );

  const handleSubmitMessage = useCallback(
    async (values: MessageFormType) => {
      try {
        const senderId = senderIdRemoteData
          .map((data) => data.person[0]?.id)
          .withDefault(undefined);
        if (!senderId) {
          toast({
            title: "Error sending message",
            status: "error",
            isClosable: true,
            description: "Unable to send message without senderId",
          });
          throw new Error("Unable to send message without senderId");
        }
        if (organizationId === "") {
          toast({
            title: "Error sending message",
            status: "error",
            isClosable: true,
            description: "Unable to send message without organization ID",
          });
          throw new Error("Unable to send message without organizationId");
        }

        for (const translation of values.messages) {
          const messageDocument = await getMessageDocument({
            subject: translation.subject,
            emailLexical: translation.email_lexical,
            smsLexical: translation.sms_lexical,
            language: translation.language,
            fileAttachments: values.email_attachments,
          });
          if (!messageDocument) {
            toast({
              title: "Error sending message",
              description: "There are no content to send",
            });
            return;
          }
          const receiversInfos =
            receiversInfosByTranslatedLanguage[translation.language ?? "en"];
          if (!receiversInfos) {
            toast({
              title: `No recipients for this language, message not sent ${translation.language_name}`,
              status: "info",
            });
            continue;
          }
          await handleSendMessageDocument(
            translation.subject || "",
            senderId,
            messageDocument,
            receiversInfos
          );
        }

        apolloClient.refetchQueries({
          include: [GET_FORM_MESSAGES],
        });

        amplitude.track("Clicked send adhoc messages", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
          translation_count: values.messages.length - 1,
        });

        toast({ title: "Messages sent" });
        navigate(-1);
      } catch (err) {
        console.error(err);
        toast.error({
          title: "Something went wrong.",
          description:
            "Error sending the message. Please try again or contact support.",
        });
      }
    },
    [
      senderIdRemoteData,
      organizationId,
      apolloClient,
      messageContext,
      sendMessageInfo.hasRecipientsWithEmail,
      sendMessageInfo.hasRecipientsWithPhone,
      toast,
      navigate,
      getMessageDocument,
      receiversInfosByTranslatedLanguage,
      handleSendMessageDocument,
    ]
  );

  useEffect(() => {
    if (emailEditor && state?.emailBody) {
      const html = convertPlainTextToHtml(state.emailBody);
      converter.fromHtml(emailEditor, html);
    }
    if (smsEditor && state?.smsBody) {
      converter.fromPlainText(smsEditor, state.smsBody);
    }
  }, [converter, emailEditor, smsEditor, state]);

  const validate = useCallback(
    async (values: MessageFormType, targetLanguages?: string[]) => {
      const { errors } = await validateMessages(
        sendMessageInfo.hasRecipientsWithEmail,
        sendMessageInfo.hasRecipientsWithPhone,
        emailEditor,
        smsEditor,
        values,
        featureFlag,
        targetLanguages
      );

      if (errors.messages && Array.isArray(errors.messages)) {
        const idx = errors.messages.findIndex(
          (m: FormikErrors<MessageContent>) => Object.keys(m).length > 0
        );
        setCurrentTranslationIndex(idx >= 0 ? idx : 0);
      }
      return errors;
    },
    [
      emailEditor,
      featureFlag,
      sendMessageInfo.hasRecipientsWithEmail,
      sendMessageInfo.hasRecipientsWithPhone,
      smsEditor,
    ]
  );
  const formik = useFormik<MessageFormType>({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmitMessage,
    validate,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
  });

  // Check and update the translated languages when the messages change
  useEffect(() => {
    setTranslatedLanguages((prev) => {
      const next = formik.values.messages
        .map((message) => message.language)
        .filter((l) => l !== "en");

      if (_.isEqual(next, prev)) {
        return prev;
      }
      return next;
    });
  }, [formik.values.messages]);

  const [hasPolished, setHasPolished] = useState(false);
  const {
    isPolishing,
    progressDrawer: polishProgressDrawer,
    previewDrawer: polishPreviewDrawer,
    handlePolish,
  } = useMessagePolish(
    sendMessageInfo.hasRecipientsWithEmail,
    sendMessageInfo.hasRecipientsWithPhone,
    messageContext
  );

  const {
    isTranslating,
    progressDrawer: translationProgressDrawer,
    previewDrawer: translationPreviewDrawer,
    handleTranslate,
  } = useMessageTranslation(
    sendMessageInfo.translatableLanguages,
    sendMessageInfo.receiversInfosByLanguage,
    sendMessageInfo.hasRecipientsWithEmail,
    sendMessageInfo.hasRecipientsWithPhone,
    messageContext
  );

  const hasTranslations = formik.values.messages.length > 1;

  const handleDeleteTranslation = useCallback(
    async (indexToRemove: number) => {
      const messages = formik.values.messages;
      if (messages.length <= 1) return;

      const message = messages[indexToRemove];
      const languageName = message?.language_name ?? "this language";

      amplitude.track("Clicked remove ad-hoc translation", {
        ...messageContext,
        has_email_content: sendMessageInfo.hasRecipientsWithEmail,
        has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        removed_language: message?.language,
      });

      const confirmed = await deleteTranslationConfirmation.confirm({
        header: `Remove ${languageName} translation?`,
        body: (
          <VStack gap={3} align="start">
            <Text>
              Removing this translation will delete all content for{" "}
              {languageName}. Recipients who preferred {languageName} will
              receive the <b>English message</b>.
            </Text>
            <Text>This action can't be undone.</Text>
          </VStack>
        ),
        confirmButton: { label: "Yes, remove" },
        cancelButton: { label: "Cancel" },
      });

      if (!confirmed) {
        amplitude.track("Clicked cancel remove ad-hoc translation", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
          removed_language: message?.language,
        });
        return;
      }

      amplitude.track("Clicked confirm remove ad-hoc translation", {
        ...messageContext,
        has_email_content: sendMessageInfo.hasRecipientsWithEmail,
        has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        removed_language: message?.language,
      });

      const nextMessages = messages.filter((_, i) => i !== indexToRemove);
      formik.setFieldValue("messages", nextMessages);
      setCurrentTranslationIndex((prev) => {
        if (indexToRemove < prev) return prev - 1;
        if (indexToRemove === prev)
          return Math.min(prev, nextMessages.length - 1);
        return prev;
      });

      toast({
        id: "translation-removed",
        title: "Translation removed",
        description: `The ${languageName} translation has been removed.`,
      });
    },
    [
      formik,
      deleteTranslationConfirmation,
      messageContext,
      sendMessageInfo.hasRecipientsWithEmail,
      sendMessageInfo.hasRecipientsWithPhone,
      toast,
    ]
  );

  const onTranslateClicked = useCallback(async () => {
    try {
      amplitude.track(
        hasTranslations
          ? "Clicked ad-hoc re-translate"
          : "Clicked ad-hoc translate",
        {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
          translatable_languages: sendMessageInfo.translatableLanguages,
        }
      );

      // Validate only the English (en) message before opening the translation dialog
      const errors = await validate(formik.values, ["en"]);
      if (Object.keys(errors).length > 0) {
        formik.setErrors(errors);
        return;
      }

      const selectedLanguages =
        sendMessageInfo.translatableLanguages.length > 1
          ? await confirmTranslationLanguages(
              sendMessageInfo.translatableLanguages.map((language) => ({
                language: language,
                hasTranslation: formik.values.messages.some(
                  (message) => message.language === language
                ),
                hasFailedTranslation: formik.values.messages.some(
                  (message) =>
                    message.language === language && !!message.error_message
                ),
              }))
            )
          : sendMessageInfo.translatableLanguages;

      if (!selectedLanguages || selectedLanguages.length === 0) {
        amplitude.track("Clicked cancel adhoc translation languages");
        return;
      }

      amplitude.track("Clicked confirm adhoc translation languages", {
        selected_languages: selectedLanguages,
        translatable_languages: sendMessageInfo.translatableLanguages,
      });

      const result = await handleTranslate(formik.values, selectedLanguages);
      if (result) {
        // Merge: update messages by language from result; add new translations if not present
        formik.setValues((prev) => {
          const nextMessages = [...prev.messages];
          for (const msg of result) {
            const idx = nextMessages.findIndex(
              (m) => m.language === msg.language
            );
            if (idx !== -1) {
              nextMessages[idx] = msg;
            } else {
              nextMessages.push(msg);
            }
          }
          return { ...prev, messages: nextMessages };
        });

        toast({
          title: `Translations confirmed`,
        });

        amplitude.track("Clicked confirm ad-hoc translation", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
          translation_count: result.length - 1,
          translated_languages: result
            .map((m) => m.language)
            .filter((l) => l !== "en"),
        });

        setCurrentTranslationIndex(0);
      } else {
        amplitude.track("Clicked cancel ad-hoc translation", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error({
        title: "Something went wrong.",
        description:
          "Error translating the message. Please try again or contact support.",
      });
    }
  }, [
    formik,
    hasTranslations,
    sendMessageInfo.hasRecipientsWithEmail,
    sendMessageInfo.hasRecipientsWithPhone,
    sendMessageInfo.translatableLanguages,
    handleTranslate,
    toast,
    messageContext,
    validate,
    confirmTranslationLanguages,
  ]);

  const onPolishClicked = useCallback(async () => {
    try {
      amplitude.track(
        hasPolished ? "Clicked ad-hoc re-polish" : "Clicked ad-hoc polish",
        {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        }
      );

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      const result = await handlePolish(formik.values);
      if (result) {
        formik.setValues({
          ...formik.values,
          messages: formik.values.messages.map((message) =>
            message.language === "en" ? result : message
          ),
        });

        amplitude.track("Clicked confirm ad-hoc polish", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        });

        toast({
          title: `Polished message inserted`,
        });

        setHasPolished(true);
        setCurrentTranslationIndex(0);
      } else {
        amplitude.track("Clicked cancel ad-hoc polish", {
          ...messageContext,
          has_email_content: sendMessageInfo.hasRecipientsWithEmail,
          has_sms_content: sendMessageInfo.hasRecipientsWithPhone,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error({
        title: "Something went wrong.",
        description:
          "Error polishing the message. Please try again or contact support.",
      });
    }
  }, [
    formik,
    hasPolished,
    messageContext,
    sendMessageInfo.hasRecipientsWithEmail,
    sendMessageInfo.hasRecipientsWithPhone,
    handlePolish,
    toast,
  ]);

  if (!state) return <NotFound />;

  const currentMessage = formik.values.messages[currentTranslationIndex];

  return (
    <FormikProvider value={formik}>
      {polishProgressDrawer}
      {polishPreviewDrawer}
      {translationProgressDrawer}
      {translationPreviewDrawer}
      {discardChangesConfirmation.confirmationDialog}
      {deleteTranslationConfirmation.confirmationDialog}
      {translationLanguageSelectorDialog}

      <Flex as={Form} direction="column" gap={6} minHeight="100%">
        <Breadcrumb
          mb={4}
          items={breadcrumb.form.getBreadcrumbsForSendMessage(
            organization,
            selectedNavFormTemplate,
            searchParams.toString()
          )}
        />
        <Heading as="h1" size="lg" pb={8}>
          Send message
        </Heading>
        <Flex direction="row" alignItems="start" gap={10}>
          <Box width="50%">
            <RecipientsTable recipients={recipients} />
          </Box>
          <Flex direction="column" pl={6} width="50%">
            <Text variant="filterHeader" mb={2}>
              Messages
            </Text>
            <Flex gap={2} mb={2}>
              <Tag
                bg="purple.500"
                color="white"
                alignSelf="flex-start"
                size="sm"
                minWidth="60px"
              >
                <TagLeftIcon as={RiLightbulbLine} />
                <TagLabel>Tip</TagLabel>
              </Tag>
              <Text>
                Type <Code>{"{{"}</Code> in any content area to see variables.
                Variables help you replace text with dynamic content.
              </Text>
            </Flex>
            {hasTranslations && (
              <Flex direction="column" gap={2} mb={4}>
                <PaginatedTabList
                  items={formik.values.messages.map((t) => {
                    const receiverCount =
                      receiversInfosByTranslatedLanguage[t.language ?? "en"]
                        ?.length ?? 0;
                    return {
                      label: `${t.language_name} (${receiverCount})`,
                      value: t.language ?? "en",
                      receiver_count: receiverCount,
                      language_name: t.language_name,
                      hasError: !!t.error_message,
                    };
                  })}
                  currentIndex={currentTranslationIndex}
                  onTabChange={setCurrentTranslationIndex}
                  tabsProps={{ overflowX: "auto", overflowY: "hidden" }}
                  renderTab={({ language_name, receiver_count, hasError }) => {
                    return (
                      <HStack spacing={1} align="center" whiteSpace="nowrap">
                        {hasError && (
                          <Tooltip label="Unable to automatically translate">
                            <Box display="inline-flex">
                              <FiAlertCircle color="red" />
                            </Box>
                          </Tooltip>
                        )}
                        <Text>{language_name}</Text>
                        <Tooltip
                          label={`${receiver_count} recipient${
                            receiver_count !== 1 ? "s" : ""
                          } prefer ${language_name}`}
                        >
                          <Text color="blackAlpha.500">({receiver_count})</Text>
                        </Tooltip>
                      </HStack>
                    );
                  }}
                />
              </Flex>
            )}
            {currentMessage?.error_message !== undefined && (
              <Alert
                status="error"
                as={Flex}
                direction="row"
                gap={2}
                alignContent="flex-start"
              >
                <AlertIcon />
                <Flex direction="column" gap={1}>
                  <AlertTitle>Unable to automatically translate</AlertTitle>
                  <AlertDescription>
                    {currentMessage?.error_message}
                  </AlertDescription>
                </Flex>
              </Alert>
            )}

            <MessageForm
              selectedMessageIndex={currentTranslationIndex}
              hasRecipientsWithEmail={sendMessageInfo.hasRecipientsWithEmail}
              hasRecipientsWithPhone={sendMessageInfo.hasRecipientsWithPhone}
              emailEditorRef={emailEditorRef}
              smsEditorRef={smsEditorRef}
            />
            <Flex direction="row" pt={8} gap={3}>
              {flags["ai-messaging-polish"].enabled && (
                <ConditionalTooltip
                  showTooltip={currentTranslationIndex !== 0}
                  label="Polishing non-english messages is not supported"
                >
                  <Button
                    onClick={onPolishClicked}
                    colorScheme="primary"
                    variant="outline"
                    leftIcon={<RiMagicLine />}
                    isLoading={isPolishing}
                    loadingText="Polishing"
                    isDisabled={currentTranslationIndex !== 0}
                  >
                    Polish writing
                  </Button>
                </ConditionalTooltip>
              )}
              {flags["ai-messaging-translation"].enabled && (
                <ConditionalTooltip
                  showTooltip={
                    sendMessageInfo.translatableLanguages.length === 0
                  }
                  label="Selected recipients have English as a preferred language"
                >
                  <Button
                    onClick={onTranslateClicked}
                    colorScheme="primary"
                    variant="outline"
                    leftIcon={<RiTranslate2 />}
                    isLoading={isTranslating}
                    loadingText="Translating..."
                    isDisabled={
                      sendMessageInfo.translatableLanguages.length === 0
                    }
                  >
                    Translate
                  </Button>
                </ConditionalTooltip>
              )}
              {currentMessage?.language !== "en" && (
                <Tooltip
                  label={`Remove the currently selected ${currentMessage?.language_name} translation`}
                  openDelay={400}
                >
                  <Button
                    aria-label={`Remove the currently selected ${currentMessage?.language_name} translation`}
                    leftIcon={<RiDeleteBin6Line />}
                    onClick={(e) => {
                      handleDeleteTranslation(currentTranslationIndex);
                    }}
                    variant="outline"
                    colorScheme="gray"
                  >
                    Remove translation
                  </Button>
                </Tooltip>
              )}
            </Flex>
            <Flex direction="row" gap={1} align="center" pt={4}>
              <MaybeLink
                to={
                  organization.hasData()
                    ? Url.OrgAdmin.AvelaLabs.aboutAvelaLabs(organization.data)
                    : undefined
                }
                isExternal
              >
                <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
                  <Icon as={RiFlaskLine} />
                  <Text>Beta</Text>
                </Tag>
              </MaybeLink>
            </Flex>
          </Flex>
        </Flex>
        <Spacer />
        <AdminFormButtons sticky>
          <Button
            onClick={async () => {
              const confirmed = await discardChangesConfirmation.confirm();
              if (confirmed) {
                navigate(-1);
              }
            }}
            variant="outline"
            colorScheme="gray"
          >
            Cancel
          </Button>
          <Spacer />
          <Button
            type="submit"
            isLoading={formik.isSubmitting || formik.isValidating}
            loadingText={"Sending..."}
          >
            Send
          </Button>
        </AdminFormButtons>
      </Flex>
    </FormikProvider>
  );
};
